/**
 * Mana Locals Admin — common API contract primitives.
 *
 * CONTRACT SOURCE: `contracts/openapi/foundation.v1.yaml` (API repository).
 * TARGET CONTRACT VERSION: foundation v1 (register v1.4).
 *
 * ---------------------------------------------------------------------------
 * PROVISIONAL — CF-03 (error envelope) is DECISION_BLOCKED
 * ---------------------------------------------------------------------------
 * The API contract register records the error envelope shape as an OPEN
 * DECISION (CF-03) with `{ error: { code, message, requestId, details? } }` as
 * the CANDIDATE form. This file implements that candidate so the client can be
 * built and tested, and it is marked provisional deliberately.
 *
 * If the API later decides a different envelope, the change is confined to this
 * file plus `src/api/client/problem.ts`. That boundedness is the reason the
 * envelope is parsed in ONE place rather than at each call site.
 *
 * Similarly CF-02 (wire casing) is DECISION_BLOCKED with camelCase as the
 * candidate. camelCase is used throughout; see `tenants.ts`.
 *
 * Nothing in this file is an authorization statement. Zod validates SHAPE only.
 */
import { z } from 'zod';

/** ISO-8601 timestamp string. Deliberately a string, not a Date: wire stays wire. */
export const IsoDateTimeSchema = z.string().min(1);

export const UuidSchema = z.string().uuid();

/**
 * One entry of the envelope's `error.details[]`.
 *
 * CONTRACT SOURCE: the API's `AgreementErrorResponseSchema` and the register's
 * §6 candidate body — `details: [{ path, code }]`. It is an ARRAY of objects,
 * NOT a map of field to messages. The two are easy to confuse and the cost of
 * confusing them is total: a map-shaped schema rejects the real body, the
 * envelope fails to parse, and the operator is shown "an unreadable error body"
 * in place of the server's own message.
 *
 * `path` is a JSON-pointer-ish field path (`/documentKey`); `code` is a stable
 * machine token (`AGREEMENT_DOCUMENT_EXISTS`). Neither carries a submitted
 * value — the API never echoes one back.
 */
export const ErrorDetailSchema = z
  .object({
    path: z.string(),
    code: z.string(),
  })
  .passthrough();

export type ErrorDetail = z.infer<typeof ErrorDetailSchema>;

/**
 * Provisional error envelope (CF-03 candidate).
 *
 * `.passthrough()` is used because the API contract explicitly permits an
 * envelope to grow; strict parsing here would turn a harmless additive field
 * into a total outage of the admin. The ENVELOPE is strict about the fields we
 * depend on; unknown siblings are tolerated.
 */
export const ApiErrorEnvelopeSchema = z.object({
  error: z
    .object({
      code: z.string().min(1),
      message: z.string().min(1),
      requestId: z.string().optional(),
      details: z.array(ErrorDetailSchema).optional(),
    })
    .passthrough(),
});

export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelopeSchema>;

/**
 * The PROVIDER's own error shape: `{ message, code }` at the TOP level.
 *
 * The authentication provider's routes (`/api/v1/auth/*`) are the provider's routes,
 * not platform operations, and they answer a refusal in this shape rather than in
 * the shared envelope. Two of its refusals — `INVALID_ORIGIN` and `EMAIL_NOT_VERIFIED`
 * — carry the SAME 403 status, so the status alone cannot tell them apart and a
 * caller that branches on the status alone will state a reason the server never gave.
 *
 * Parsed here, beside the envelope, so both wire shapes are described in one place
 * and no module has to re-derive either.
 */
export const ProviderErrorSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .passthrough();

export type ProviderError = z.infer<typeof ProviderErrorSchema>;

/**
 * A cursor-paginated collection.
 *
 * NOT YET BACKED BY A CONTRACT. The API currently exposes no list endpoint, so
 * this shape is a UI-owned proposal. It is stated here in one place so that
 * when the API contracts its first paginated collection, the change is a single
 * edit rather than a scattered re-derivation across every list.
 *
 * `PageRequestSchema` is what `AppListLayout` sends; `PageSchema` is what it
 * expects back.
 */
export const PageRequestSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(25),
  sortBy: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  search: z.string().optional(),
});

export type PageRequest = z.infer<typeof PageRequestSchema>;

/** NOT YET BACKED BY A CONTRACT — UI-owned proposal, see above. */
export function PageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  });
}

/**
 * The correlation envelope every API response carries.
 *
 * CONTRACT SOURCE: the API's `MetaSchema` (`{ requestId }`), which is the same
 * shape in M13, M44, M66 and the platform tenant list. Parsed once here so no
 * module re-declares it.
 */
export const ResponseMetaSchema = z.object({
  requestId: z.string().min(1),
});

/**
 * The cursor pagination block a collection response carries.
 *
 * CONTRACT SOURCE: the API's `PageSchema` (`{ nextCursor, hasMore }`). `nextCursor`
 * is REQUIRED and nullable — null on the last page.
 */
export const CollectionPageSchema = z.object({
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

/**
 * The collection envelope: `{ data, page, meta }`.
 *
 * CONTRACT SOURCE: the API's list-response shape, identical across M13's
 * `OfferListResponseSchema`, M44's `MediaAssetListResponseSchema` and M66's
 * `CanonicalProductListResponseSchema`.
 */
export function CollectionResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    page: CollectionPageSchema,
    meta: ResponseMetaSchema,
  });
}

export type Page<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

/**
 * Deployment version manifest emitted into `/version.json` at build time.
 *
 * Parsed at the boundary like every other wire payload so a corrupt CDN error
 * page (e.g. an HTML 502) is never mistaken for a valid version string.
 */
export const BuildVersionSchema = z.object({
  buildId: z.string().min(1),
});

export type BuildVersion = z.infer<typeof BuildVersionSchema>;
